import {watch} from "vue";
import {allowExternal, consent, openPreferences, placeholderTexts} from "./Consent";

/**
 * A safety net for the iframes of other sites that come in the text of a page (the editor can paste a video or a map
 * into the text): until the visitor allows the external content, they are replaced by the same notice that the
 * maps and videos of the application show. The embeds of the application itself are blocked in their components.
 */
export const guardExternalIframes = (): void => {
	const blocked: { placeholder: HTMLElement, iframe: HTMLIFrameElement }[] = [];

	const isExternal = (iframe: HTMLIFrameElement): boolean => {
		try {
			const url = new URL(iframe.getAttribute('src') ?? '', window.location.href);
			return /^https?:$/.test(url.protocol) && url.origin !== window.location.origin;
		} catch (e) {
			return false;
		}
	};

	const placeholderFor = (): HTMLElement => {
		const texts = placeholderTexts('content');
		const box = document.createElement('div');
		box.className = 'consent-placeholder';
		box.setAttribute('role', 'note');

		const text = document.createElement('p');
		text.className = 'consent-placeholder__text';
		text.textContent = texts.text;

		const actions = document.createElement('div');
		actions.className = 'consent-placeholder__actions';
		const allow = document.createElement('button');
		allow.type = 'button';
		allow.className = 'consent-placeholder__button';
		allow.textContent = texts.allow;
		allow.addEventListener('click', allowExternal);
		const settings = document.createElement('button');
		settings.type = 'button';
		settings.className = 'consent-placeholder__link';
		settings.textContent = texts.settings;
		settings.addEventListener('click', openPreferences);

		actions.append(allow, settings);
		box.append(text, actions);
		return box;
	};

	const block = (iframe: HTMLIFrameElement): void => {
		if (consent.external || !isExternal(iframe)) {
			return;
		}
		const placeholder = placeholderFor();
		iframe.replaceWith(placeholder);
		blocked.push({placeholder, iframe});
	};

	new MutationObserver((records) => records.forEach((record) => record.addedNodes.forEach((node) => {
		if (node instanceof HTMLIFrameElement) {
			block(node);
		} else if (node instanceof Element) {
			node.querySelectorAll('iframe').forEach(block);
		}
	}))).observe(document.documentElement, {childList: true, subtree: true});

	watch(() => consent.external, (allowed) => {
		if (allowed) {
			blocked.splice(0).forEach(({placeholder, iframe}) => placeholder.replaceWith(iframe));
		}
	});
};
